import { SarasIQ, StarShape } from "./icons";
import {
  NAV_BRAND_HEADER_CLASS,
  NAV_BRAND_TEXT_CLASS,
  NAV_RAIL_CONTAINER_CLASS,
  NAV_RAIL_PADDING,
  NAV_SKELETON_CLASS,
} from "./navStyles";
import { cn } from "@/lib/utils";

/**
 * The optimistic boot shell — what paints while the warehouse / RBAC calls are
 * still in flight, in place of the full-screen GrainLoader (boot LCP plan,
 * Phase 2). It is a STATIC frame: no data hooks, no providers, and above all no
 * `NavMain`/`AppRail` — those call openui's `useThreadListState()` and throw
 * "chat context not found" outside the `Threads` `<ChatProvider>` shell.
 *
 * The headline is REAL text, not a skeleton bar, so it is the LCP element and
 * the metric stops at first shell paint rather than at the end of the boot
 * chain. It is copied verbatim (markup, classes, wrapper class names — the
 * `.text-gradient` rule in index.css is scoped to
 * `.iq-suggestions .iq-suggestion-headline`) from `Suggestions.tsx`, and the
 * surrounding frame mirrors `Threads.tsx` → `Chat.tsx`, so the swap to the real
 * shell lands the headline in the same place at the same size — no visible jump.
 *
 * Everything below the headline is a shimmer block on the same `--dash-*` /
 * `--rail-*` tokens the real shell uses, so it reads correctly in light/dark and
 * in every dash preset. Shimmer is `motion-safe:` only.
 */

/** Content-area shimmer block (mirrors DashboardBodySkeleton's BLOCK idiom). */
const BLOCK =
  "rounded-[10px] bg-[var(--dash-surface-2,var(--structural-neutral-2))] motion-safe:animate-pulse";

/** Rail shimmer block — band-ink mix, so it reads on the dark band. */
const RAIL_BLOCK = cn("rounded-[9px] motion-safe:animate-pulse", NAV_SKELETON_CLASS);

export function BootShellSkeleton() {
  return (
    <div
      className="iq-chat-layout flex h-screen overflow-x-hidden relative"
      style={{ background: "var(--dash-paper, var(--structural-primary-tint))" }}
      role="status"
      aria-busy="true"
      aria-label="Loading Saras iQ"
    >
      {/* Left rail frame. Hidden below md, matching the real shell (the rail is
          offcanvas on mobile). Width tracks SIDEBAR_WIDTH (16.25rem). */}
      <div
        className={cn(
          "hidden w-[16.25rem] shrink-0 flex-col md:flex",
          NAV_RAIL_PADDING,
          NAV_RAIL_CONTAINER_CLASS,
        )}
      >
        <div className={NAV_BRAND_HEADER_CLASS}>
          <div className="flex items-center relative">
            <SarasIQ size={35} className="text-[color:var(--dash-accent)] relative" />
            <h2 className={cn("px-2 text-3xl", NAV_BRAND_TEXT_CLASS)}>iQ</h2>
            <StarShape size={12} className="absolute top-1 right-0" />
          </div>
        </div>
        {/* New Chat CTA + the destination/recents stack, as blocks. */}
        <div className={cn(RAIL_BLOCK, "mb-4 h-9 w-full")} />
        <div className="flex flex-col gap-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={cn(RAIL_BLOCK, "h-8 w-full")} />
          ))}
        </div>
        <div className="mt-4 flex flex-col gap-[2px]">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={cn(RAIL_BLOCK, "h-7 w-full")} />
          ))}
        </div>
      </div>

      <main className="iq-chat-content flex flex-col flex-1 min-w-full md:min-w-auto relative overflow-hidden">
        {/* Breadcrumb strip placeholder — same geometry as AppBreadcrumb. */}
        <div className="hidden h-9 shrink-0 items-center border-b px-4 border-[var(--dash-hairline,var(--grey-200))] bg-[var(--dash-paper,var(--structural-white))] md:flex">
          <div className={cn(BLOCK, "h-3 w-[120px]")} />
        </div>
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <div className="iq-wrapper m-0 lg:m-[12px] lg:ml-3 box-border border border-[color:var(--dash-hairline)] rounded-lg h-full lg:h-[calc(100%-24px)]">
            <div className="iq-chat h-full flex flex-col bg-[var(--dash-surface)] rounded-lg lg:border border-solid border-[color:var(--dash-hairline)]">
              {/* No `items-center`: the real Suggestions wrapper is top-anchored
                  (its container's own pt-12/md:py-16 sets the offset), so the
                  greeting must sit at the same y for a jump-free swap. */}
              <div className="iq-suggestions flex flex-1 min-h-0 justify-center overflow-hidden px-2 md:p-0 md:w-[calc(100%-32px)] md:max-w-[786px] md:mx-auto">
                <div className="iq-suggestions-container max-h-full pt-12 md:py-16 md:px-16 w-[calc(100%-32px)] md:w-full">
                  <div className="iq-suggestion-headline">
                    {/* The LCP element. Verbatim from Suggestions.tsx. */}
                    <h1 className="text-[22px] md:text-[26px] font-[650] tracking-[-0.01em] text-[color:var(--dash-ink,var(--text-high-emphasis))]">
                      Meet{" "}
                      <span className="relative pr-3 text-gradient">
                        Saras iQ
                        <StarShape size={12} className="absolute top-1 right-0" />
                      </span>
                      <br className="md:hidden" />
                      <span className="hidden md:inline">, </span>
                      Your e-commerce AI analyst
                    </h1>
                    <p className="mt-2 text-[13px] md:text-sm text-[color:var(--dash-muted,var(--text-medium-emphasis))]">
                      Ask about sales, customers, or marketing performance — or start from one of these.
                    </p>
                  </div>
                  {/* Suggestion cards — real questions arrive with the session, so
                      these stay blocks at the card's reserved min height (66px). */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-3 mt-4 md:mt-6">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="min-h-[66px] rounded-[14px] border border-[color:var(--dash-hairline,var(--grey-200))] bg-[var(--dash-surface,var(--structural-white))] px-3.5 py-3"
                      >
                        <div className={cn(BLOCK, "h-3.5 w-3/4")} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Composer strip placeholder. */}
              <div className="iq-chat-content-width flex flex-col gap-3 mb-2 mx-auto w-full px-4">
                <div className={cn(BLOCK, "h-[52px] w-full rounded-[14px]")} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default BootShellSkeleton;
